import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Share2, Globe, Target, Brain, Smartphone, BookOpen, Users, Award, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo-northquest.svg";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();

  const serviceLinks = [
    { 
      to: "/services/social-media", 
      label: "Social Media Marketing", 
      description: "Creating authentic connections through creativity, strategy, and data-driven insights",
      icon: Share2
    },
    { 
      to: "/services/website", 
      label: "Website Design & Development", 
      description: "Blend creative strategy and technology to simplify and scale your digital presence",
      icon: Globe
    },
    { 
      to: "/services/digital-marketing", 
      label: "Integrated Digital Marketing", 
      description: "Audience-focused strategies that engage, convert, and grow your business",
      icon: Target
    },
    { 
      to: "/services/ai", 
      label: "AI Marketing Solutions", 
      description: "Leverage AI with strategic marketing to work smarter, faster, and more profitably",
      icon: Brain
    },
    { 
      to: "/services/mobile-app", 
      label: "Mobile App Development", 
      description: "Strengthen customer loyalty with apps that create personalized engagement",
      icon: Smartphone
    },
  ];

  const resourceLinks = [
    {
      to: "/about",
      label: "About",
      description: "Our story and values",
      icon: BookOpen
    },
    {
      to: "/commitments",
      label: "Commitments",
      description: "Our pledge to sustainability",
      icon: Award
    },
  ];

  const actionLinks = [
    {
      to: "/contact",
      label: "Get Started",
      description: "Schedule a consultation",
      icon: Mail
    },
    {
      to: "/blog",
      label: "Insights",
      description: "Latest marketing trends",
      icon: BookOpen
    },
  ];

  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const heroHeight = window.innerHeight * 0.5;
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > heroHeight) {
        setIsScrolled(true);
        // If user scrolls after expanding, collapse it again
        if (Math.abs(currentScrollY - lastScrollY) > 5) {
          setIsExpanded(false);
        }
      } else {
        setIsScrolled(false);
        setIsExpanded(false);
      }
      
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { to: "/", label: "Home", hasDropdown: false },
    { to: "/about", label: "About", hasDropdown: false },
    { to: "/services", label: "Services", hasDropdown: false },
    { to: "/blog", label: "Blog", hasDropdown: false },
    { to: "/commitments", label: "Commitments", hasDropdown: false },
  ];

  const isActive = (path: string) => location.pathname === path;

  const isCompact = isScrolled && !isExpanded;

  return (
    <nav 
      onClick={() => isScrolled && !isOpen && setIsExpanded(!isExpanded)}
      className={`fixed left-1/2 -translate-x-1/2 z-50 bg-black/95 shadow-2xl border border-white/10 transition-all duration-300 ${
        isCompact ? 'top-2 w-[calc(100%-1rem)] sm:w-[85%] md:w-[75%] lg:w-[70%] max-w-4xl rounded-full cursor-pointer' : 'top-2 md:top-4 w-[calc(100%-1rem)] sm:w-[95%] max-w-6xl rounded-2xl md:rounded-full'
      } ${isOpen ? 'rounded-2xl' : ''}`}
    >
      <div className={`transition-all duration-300 ${isCompact ? 'px-4 sm:px-6 md:px-8' : 'px-4 sm:px-6 md:px-8'}`}>
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isCompact ? 'h-12 sm:h-14' : 'h-16 sm:h-18 md:h-20'
        }`}>
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <img 
              src={logo} 
              alt="Northquest Logo" 
              className={`transition-all group-hover:scale-105 ${
                isCompact ? 'h-10 sm:h-9' : 'h-12 sm:h-11 md:h-12'
              }`}
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className={`hidden lg:flex items-center justify-center flex-1 transition-all ${
            isCompact ? 'space-x-3 xl:space-x-4' : 'space-x-6 xl:space-x-8'
          }`}>
            {links.map((link) => {
              if (link.to === "/services") {
                return (
                  <div 
                    key={link.to}
                    className="relative"
                    onMouseEnter={() => setServicesDropdownOpen(true)}
                    onMouseLeave={() => setServicesDropdownOpen(false)}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setServicesDropdownOpen(!servicesDropdownOpen);
                      }}
                      className={`flex items-center gap-1 hover:text-white font-medium transition-all relative ${
                        isCompact ? 'text-xs' : 'text-sm'
                      } ${isActive(link.to) || location.pathname.startsWith('/services/') ? 'text-white after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-accent' : 'text-white/70'}`}
                    >
                      {link.label}
                      <ChevronDown size={isCompact ? 14 : 16} className={`transition-transform ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Mega Menu */}
                    <div 
                      className={`absolute left-1/2 -translate-x-1/2 top-full mt-8 transition-all duration-300 ${
                        servicesDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                      style={{ width: '800px', maxWidth: '95vw' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="bg-black border border-white/10 rounded-2xl shadow-2xl p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                          {/* Our Services Column */}
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <div className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                                Our Services
                              </div>
                              <ChevronDown size={14} className="text-white/60 rotate-[-90deg]" />
                            </div>
                            <div className="space-y-3">
                              {serviceLinks.map((service) => {
                                const Icon = service.icon;
                                return (
                                  <Link
                                    key={service.to}
                                    to={service.to}
                                    onClick={() => setServicesDropdownOpen(false)}
                                    className="group block p-3 rounded-xl hover:bg-white/5 transition-all"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="p-2 rounded-lg bg-white/10 text-white group-hover:bg-accent group-hover:text-accent-foreground transition-all flex-shrink-0">
                                        <Icon size={18} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-white group-hover:text-accent transition-colors mb-0.5">
                                          {service.label}
                                        </div>
                                        <div className="text-xs text-white/60 line-clamp-2">
                                          {service.description}
                                        </div>
                                      </div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>

                          {/* Get Started Column */}
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <div className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                                Let's Connect
                              </div>
                              <ChevronDown size={14} className="text-white/60 rotate-[-90deg]" />
                            </div>
                            <div className="space-y-3">
                              {actionLinks.map((action) => {
                                const Icon = action.icon;
                                return (
                                  <Link
                                    key={action.to}
                                    to={action.to}
                                    onClick={() => setServicesDropdownOpen(false)}
                                    className="group block p-3 rounded-xl hover:bg-white/5 transition-all"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="p-2 rounded-lg bg-white/10 text-white group-hover:bg-accent group-hover:text-accent-foreground transition-all flex-shrink-0">
                                        <Icon size={18} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-white group-hover:text-accent transition-colors mb-0.5">
                                          {action.label}
                                        </div>
                                        <div className="text-xs text-white/60 line-clamp-2">
                                          {action.description}
                                        </div>
                                      </div>
                                    </div>
                                  </Link>
                                );
                              })}
                              
                              {/* Featured CTA */}
                              <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                                <div className="text-sm font-semibold text-white mb-2">
                                  View All Services
                                </div>
                                <p className="text-xs text-white/60 mb-3">
                                  Explore our comprehensive range of marketing solutions
                                </p>
                                <Link
                                  to="/services"
                                  onClick={() => setServicesDropdownOpen(false)}
                                  className="inline-flex items-center gap-2 text-xs font-semibold text-accent hover:underline"
                                >
                                  Learn More
                                  <ChevronDown size={12} className="rotate-[-90deg]" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(e) => e.stopPropagation()}
                  className={`flex items-center gap-1 hover:text-white font-medium transition-all relative ${
                    isCompact ? 'text-xs' : 'text-sm'
                  } ${isActive(link.to) ? 'text-white after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-accent' : 'text-white/70'}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block flex-shrink-0">
            <Button 
              asChild 
              size={isCompact ? "sm" : "default"} 
              className={`rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold transition-all whitespace-nowrap ${
                isCompact ? 'px-4 xl:px-6 text-xs' : 'px-6 xl:px-8'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <Link to="/contact">Inquire Now</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="lg:hidden p-1.5 sm:p-2 rounded-xl hover:bg-white/10 transition-colors text-white flex-shrink-0"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out border-t border-white/10 ${
            isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 border-transparent'
          }`}
        >
          <div className={`py-3 sm:py-4 transition-transform duration-300 ${
            isOpen ? 'translate-y-0' : '-translate-y-4'
          }`}>
            <div className="flex flex-col space-y-1 sm:space-y-2">
              {links.map((link, index) => {
                if (link.to === "/services") {
                  return (
                    <div key={link.to}>
                      <button
                        onClick={() => setServicesOpen(!servicesOpen)}
                        className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-sm font-medium hover:text-white hover:bg-white/10 transition-all duration-200 ${
                          isActive(link.to) || location.pathname.startsWith('/services/') ? 'text-white bg-white/5' : 'text-white/70'
                        } ${isOpen ? 'animate-fade-in' : ''}`}
                        style={{
                          animationDelay: `${index * 50}ms`
                        }}
                      >
                        {link.label}
                        <ChevronDown size={16} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${
                        servicesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="pl-4 mt-1 space-y-1">
                          <Link
                            to="/services"
                            onClick={() => {
                              setIsOpen(false);
                              setServicesOpen(false);
                            }}
                            className="block px-3 py-2 text-xs font-semibold text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                          >
                            All Services
                          </Link>
                          {serviceLinks.map((service) => {
                            const Icon = service.icon;
                            return (
                              <Link
                                key={service.to}
                                to={service.to}
                                onClick={() => {
                                  setIsOpen(false);
                                  setServicesOpen(false);
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                              >
                                <Icon size={14} className="flex-shrink-0" />
                                <span>{service.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-sm font-medium hover:text-white hover:bg-white/10 transition-all duration-200 ${
                      isActive(link.to) ? 'text-white bg-white/5' : 'text-white/70'
                    } ${isOpen ? 'animate-fade-in' : ''}`}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className={`pt-2 transition-all duration-300 ${isOpen ? 'animate-fade-in' : ''}`} style={{ animationDelay: `${links.length * 50}ms` }}>
                <Button asChild className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90" size="sm">
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    Inquire Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
